/**
 * 時間處理工具函數
 * 統一處理台灣時區的時間轉換，避免時區問題
 */

const TAIWAN_TIMEZONE = 'Asia/Taipei';
const TAIWAN_OFFSET_MS = 8 * 60 * 60 * 1000; // 8 小時的毫秒數

/**
 * 解析台灣時區的時間字串
 * 處理 SQL Server datetime 在 JSON 序列化時轉換為 UTC 的問題
 * 
 * @param dateStr - 時間字串，可能是：
 *   - ISO UTC 格式: '2026-03-05T00:00:00.000Z'
 *   - ISO 格式: '2026-03-05T00:00:00'
 *   - 標準格式: '2026-03-05 00:00:00'
 * @returns Date 物件（正確的台灣時間）
 */
export const parseTaiwanTime = (dateStr: string): Date => {
    if (!dateStr) {
        throw new Error('時間字串不能為空');
    }

    // 情況 1: ISO UTC 格式（SQL Server JSON 序列化的結果）
    // 例如: '2026-03-05T00:00:00.000Z'
    if (dateStr.includes('Z')) {
        // SQL Server 將台灣時間存為 UTC，需要還原
        // 將 UTC 時間減去 8 小時，得到正確的台灣時間
        const utcDate = new Date(dateStr);
        return new Date(utcDate.getTime() - TAIWAN_OFFSET_MS);
    }

    // 情況 2: ISO 格式但沒有時區標記
    // 例如: '2026-03-05T00:00:00'
    if (dateStr.includes('T')) {
        // 檢查是否已有時區標記
        const hasTimezone = dateStr.includes('+') || 
                           (dateStr.match(/-/g) || []).length > 2; // 檢查是否有時區偏移
        
        if (!hasTimezone) {
            // 沒有時區標記，假設是台灣時間
            return new Date(dateStr + '+08:00');
        }
        return new Date(dateStr);
    }

    // 情況 3: 標準格式 'YYYY-MM-DD HH:mm:ss'
    // 例如: '2026-03-05 00:00:00'
    // 明確指定為台灣時區
    const isoStr = dateStr.replace(' ', 'T') + '+08:00';
    return new Date(isoStr);
};

/**
 * 格式化時間為台灣時區的字串
 * @param date - Date 物件
 * @param format - 格式類型：'full' | 'date' | 'time' | 'datetime'
 * @returns 格式化後的字串
 */
export const formatTaiwanTime = (
    date: Date, 
    format: 'full' | 'date' | 'time' | 'datetime' = 'full'
): string => {
    const options: Intl.DateTimeFormatOptions = {
        timeZone: TAIWAN_TIMEZONE,
    };

    switch (format) {
        case 'date':
            options.year = 'numeric';
            options.month = '2-digit';
            options.day = '2-digit';
            break;
        case 'time':
            options.hour = '2-digit';
            options.minute = '2-digit';
            options.second = '2-digit';
            break;
        case 'datetime':
            options.year = 'numeric';
            options.month = '2-digit';
            options.day = '2-digit';
            options.hour = '2-digit';
            options.minute = '2-digit';
            options.second = '2-digit';
            break;
        case 'full':
        default:
            options.year = 'numeric';
            options.month = '2-digit';
            options.day = '2-digit';
            options.hour = '2-digit';
            options.minute = '2-digit';
            options.second = '2-digit';
            options.hour12 = true;
    }

    return date.toLocaleString('zh-TW', options);
};

/**
 * 獲取當前台灣時間
 */
export const getCurrentTaiwanTime = (): Date => {
    return new Date();
};

/**
 * 比較兩個時間（台灣時區）
 * @returns 正數表示 date1 > date2，負數表示 date1 < date2，0 表示相等
 */
export const compareTaiwanTime = (date1: Date, date2: Date): number => {
    return date1.getTime() - date2.getTime();
};

/**
 * 檢查時間是否在範圍內（台灣時區）
 */
export const isTimeInRange = (
    currentTime: Date,
    beginTime: Date,
    endTime: Date
): boolean => {
    return currentTime >= beginTime && currentTime <= endTime;
};

